<template>
  <MatchingTaxonomiesDataProvider v-slot="{ industries, decisionLevels }">
    <MatchesDataProvider v-slot="{ matches, loading, failed, matchCount }">
      <ParticipantDashboardLayout>
        <div class="p-6 md:p-8 space-y-6 w-full">
          <!-- Top Header Row -->
          <header class="flex flex-col gap-6">
            <div class="flex items-start gap-3 min-w-0">
              <button
                type="button"
                class="shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base rounded-xl border border-slate-200 text-slate-600 hover:text-teal-600 hover:border-teal-300 transition-colors"
                aria-label="Back to dashboard"
                title="Back to dashboard"
                @click="$router.push({ name: 'dashboard' }).catch(() => {})"
              >
                <i class="fal fa-arrow-left"></i>
              </button>
              <div class="min-w-0">
                <h1
                  class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                >
                  {{ $t('Views.AiMatching.Matches.title') }}
                </h1>
                <p class="text-sm text-slate-500 mt-1">
                  {{ $t('Views.AiMatching.Matches.subtitle') }}
                </p>
              </div>
            </div>
            <NetworkingQuickNav />
          </header>

          <!-- Filters: search pill + dropdown pills (matches Open Sessions) -->
          <div class="flex flex-col lg:flex-row lg:items-center gap-3">
            <SearchPill
              v-model="filters.search"
              :placeholder="$t('Views.AiMatching.Matches.searchPlaceholder')"
              class="w-full lg:flex-1 lg:min-w-0"
            />

            <div class="grid grid-cols-2 sm:flex sm:flex-row gap-2 lg:ml-auto">
              <select
                v-model="filters.industryId"
                class="w-full sm:w-auto sm:min-w-[11rem] pl-5 bg-slate-50 text-slate-700 text-sm font-semibold rounded-full border border-slate-200 focus:border-teal-500 outline-none transition-all cursor-pointer min-h-[44px]"
              >
                <option value="">
                  {{ $t('Views.AiMatching.Matches.filterIndustry') }}
                </option>
                <option v-for="i in industries" :key="i.id" :value="i.id">
                  {{ i.label }}
                </option>
              </select>

              <select
                v-model="filters.decisionLevelId"
                class="w-full sm:w-auto sm:min-w-[11rem] pl-5 bg-slate-50 text-slate-700 text-sm font-semibold rounded-full border border-slate-200 focus:border-teal-500 outline-none transition-all cursor-pointer min-h-[44px]"
              >
                <option value="">
                  {{ $t('Views.AiMatching.Matches.filterDecisionLevel') }}
                </option>
                <option v-for="d in decisionLevels" :key="d.id" :value="d.id">
                  {{ d.label }}
                </option>
              </select>

              <select
                v-model="filters.sort"
                class="ai-filter-wide w-full sm:w-auto sm:min-w-[11rem] pl-5 bg-slate-50 text-slate-700 text-sm font-semibold rounded-full border border-slate-200 focus:border-teal-500 outline-none transition-all cursor-pointer min-h-[44px]"
              >
                <option value="score">
                  {{ $t('Views.AiMatching.Matches.sortByScore') }}
                </option>
                <option value="name">
                  {{ $t('Views.AiMatching.Matches.sortByName') }}
                </option>
              </select>
            </div>
          </div>

          <!-- Matches State Handling -->
          <div
            v-if="loading"
            class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-slate-500 shadow-sm"
          >
            <i class="fal fa-spinner-third animate-spin mr-2"></i>
            {{ $t('Views.AiMatching.Matches.loading') }}
          </div>

          <div
            v-else-if="failed"
            class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-red-500 px-4 shadow-sm"
          >
            {{ $t('Views.AiMatching.Matches.loadFailed') }}
          </div>

          <div
            v-else-if="matchCount === 0"
            class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-slate-500 px-4 shadow-sm"
          >
            {{ $t('Views.AiMatching.Matches.empty') }}
          </div>

          <div
            v-else
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in"
          >
            <MatchCard
              v-for="m in filteredMatches(matches)"
              :key="m.matchId"
              :match="m"
              @open="openMatch"
              @book="bookMeeting"
            />
          </div>
        </div>
      </ParticipantDashboardLayout>
    </MatchesDataProvider>
  </MatchingTaxonomiesDataProvider>
</template>

<script>
import MatchCard from '@/components/aiMatching/MatchCard.vue';
import BookMeetingModal from '@/components/sponsor/BookMeetingModal';
import ParticipantDashboardLayout from '@/components/shared/ParticipantDashboardLayout.vue';
import NetworkingQuickNav from '@/components/aiMatching/NetworkingQuickNav.vue';
import SearchPill from '@/components/shared/SearchPill.vue';
import { englishOnlyI18nMixin } from '@/mixins';

export default {
  name: 'AiMatches',
  components: {
    MatchCard,
    ParticipantDashboardLayout,
    NetworkingQuickNav,
    SearchPill
  },
  mixins: [englishOnlyI18nMixin],
  data() {
    return {
      filters: {
        search: '',
        industryId: '',
        decisionLevelId: '',
        sort: 'score'
      }
    };
  },
  methods: {
    filteredMatches(matches) {
      const q = this.filters.search.trim().toLowerCase();
      let out = matches.filter((m) => {
        if (
          this.filters.industryId &&
          m.sponsorIndustryId !== this.filters.industryId
        ) {
          return false;
        }
        if (q) {
          const hay = `${m.sponsorName || ''} ${
            m.sponsorDescription || ''
          }`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });

      if (this.filters.sort === 'name') {
        out = [...out].sort((a, b) =>
          (a.sponsorName || '').localeCompare(b.sponsorName || '')
        );
      } else {
        out = [...out].sort(
          (a, b) => (b.calibratedScore || 0) - (a.calibratedScore || 0)
        );
      }

      return out;
    },
    openMatch(match) {
      this.$router.push({
        name: 'ai-match-sponsor',
        params: { matchId: match.matchId }
      });
    },
    bookMeeting(match) {
      if (!match || !match.sponsorId) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        BookMeetingModal,
        {
          sponsorId: match.sponsorId,
          sponsorName: match.sponsorName || '',
          boothLabel: match.sponsorBoothLabel || '',
          english: true
        },
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 600,
          width: '90%',
          height: 'auto'
        }
      );
    }
  }
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Limit all Font Awesome icons/SVGs to avoid giant expansion */
i.fal,
i.fas,
i.far,
i.fab,
svg {
  width: 1.125rem !important;
  height: 1.125rem !important;
  min-width: 1.125rem !important;
  min-height: 1.125rem !important;
  max-width: 1.125rem !important;
  max-height: 1.125rem !important;
  font-size: 1.125rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

/* Spinner Icon */
.fa-spinner-third {
  width: 1.25rem !important;
  height: 1.25rem !important;
  min-width: 1.25rem !important;
  min-height: 1.25rem !important;
  font-size: 1.25rem !important;
}

/* On mobile the filters are a 2-col grid (Industry · Decision level);
   the Score select spans the full width on its own row below them. */
@media (max-width: 639.98px) {
  .ai-filter-wide {
    grid-column: 1 / -1;
  }
}

/* Beautiful Focus States for Filters Bar */
input:focus,
select:focus {
  border-color: var(--color-primary) !important;
  box-shadow: 0 0 0 3px rgb(var(--brand-ramp-600) / 0.15) !important;
  background-color: #ffffff !important;
}

/* Custom Dropdown select indicator to avoid native visual shift */
select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%2364748b'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpolyline%20points='6%209%2012%2015%2018%209'%3E%3C/polyline%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.875rem center !important;
  background-size: 1rem !important;
  padding-right: 2.25rem !important;
}
</style>
